import { Controller } from "@hotwired/stimulus"
import { SECTIONS_PARAM, QUESTIONS_PARAM, FOCUS_PARAM, paramFromUrl, parseSpec, specMatches, describeSpec } from "./reading_focus"

/**
 * Chapter Outline Controller
 *
 * Scans the content wrapper for h2 and h3 elements,
 * assigns section numbers (1.0, 1.1, etc.), adds anchor IDs,
 * and injects a collapsible, clickable TOC outline.
 *
 * Reading focus: when the page URL carries `?sections=…` (or the `sections`
 * value is set), the parts NOT selected are marked `.focus-muted` and, by
 * default, FOLDED into collapsed <details> accordions ("Not assigned · 0.9 …")
 * that open in place; `?focus=dim` keeps them in the flow but dimmed instead.
 * The outline marks them `.toc-unassigned`, and an "Assigned reading" banner
 * with a "Show full chapter" toggle is inserted after the outline. The numbering
 * and the focus live in the same controller on purpose: the link uses the
 * numbers this controller displays, so they can never disagree. Grammar and
 * rationale: reading_focus.js, docs/book-sections.md.
 *
 * Usage: add data-controller="chapter-outline" to the theme wrapper element.
 * Optional: data-chapter-outline-scroll-offset-value="65"
 *           data-chapter-outline-insert-after-value=".learning-objectives"
 *           data-chapter-outline-wrapper-selector-value=".module-content-wrapper"
 *
 * wrapperSelector defaults to ".health-content-wrapper" (the wellness theme).
 * Themes with a different content wrapper class (e.g. ".module-content-wrapper"
 * for theme-finance) override it via the data attribute above. The default
 * accepts a comma-separated list, so any known wrapper is matched out of the box.
 *
 * headingLevels picks which two heading tags are the section / sub-section
 * levels. Our own themes author h2/h3; an OpenStax page uses its h2 for the
 * page title and h3/h4 for sections, so the OpenStax reader passes "h3,h4".
 * Internally the two levels are still called "h2" and "h3" (major / minor).
 * excludeSelector skips headings inside matching containers (OpenStax puts
 * h3 titles inside learning-objective and feature-note boxes).
 */
export default class extends Controller {
  static values = {
    scrollOffset: { type: Number, default: 65 },
    insertAfter: { type: String, default: ".learning-objectives" },
    wrapperSelector: { type: String, default: ".health-content-wrapper, .module-content-wrapper" },
    headingLevels: { type: String, default: "h2,h3" },
    excludeSelector: { type: String, default: "" },
    // false = no section numbers at all: every heading behaves like
    // class="no-number" (anchor + outline entry, addressable by slug). The
    // OpenStax reader uses this — OpenStax already numbers pages ("1.3") and
    // figures ("Figure 1.2"), and a third scheme on sub-sections collided with
    // both (a "3" chip on page 1.1 read as "1.3").
    numbered: { type: Boolean, default: true },
    // Chapter-relative numbering. Set to e.g. "3" so sections number 3.1, 3.2 …
    // and sub-sections 3.1.1, 3.1.2 …, matching a numbered-textbook scheme.
    // Empty (default) keeps the per-asset 1.0, 2.0 … scheme (the wellness theme).
    chapter: { type: String, default: "" },
    // Ordinal section numbering — opt-in (theme-appraisal). Numbers sections
    // 1, 2, 3 … and sub-sections 1.1, 1.2 … WITHOUT the chapter prefix, so the
    // section pills (e.g. "6") never collide with the book's own exhibit numbers
    // (e.g. "Exhibit 14.6"). When true, the `chapter` value above is ignored for
    // display. Default false keeps the chapter.section scheme (finance uses it).
    ordinalSections: { type: Boolean, default: false },
    // Scroll-spy: highlight the TOC item whose section is currently in view.
    // Opt-in (used by theme-appraisal) so wellness/finance behavior is unchanged.
    activeTracking: { type: Boolean, default: false },
    // Reading focus. Empty (default) falls back to the page URL's `sections` /
    // `questions` params; a server-rendered value (e.g. a saved reading) wins.
    sections: { type: String, default: "" },
    questions: { type: String, default: "" },
    // "fold" (default) collapses unassigned parts into accordions; "dim" leaves
    // them in the flow at reduced opacity. Falls back to the URL's `focus` param.
    focusMode: { type: String, default: "" }
  }

  connect() {
    const wrapper = this.element.querySelector(this.wrapperSelectorValue)
    if (!wrapper) return

    const [majorTag, minorTag] = this.headingLevelsValue.split(",").map((t) => t.trim().toLowerCase())
    const headings = wrapper.querySelectorAll(`${majorTag}, ${minorTag || majorTag}`)
    if (!headings.length) return
    const exclude = this.excludeSelectorValue

    const tocItems = []
    const ordinal = this.ordinalSectionsValue
    const chapter = this.chapterValue
    let h2Count = 0
    let h3Count = 0

    headings.forEach((heading) => {
      // Skip empty headings (the &nbsp; ones)
      const text = heading.textContent.trim().replace(/\u00a0/g, "")
      if (!text) return
      if (exclude && heading.closest(exclude)) return

      // normalized level: "h2" = section, "h3" = sub-section, whatever the tags
      const level = heading.tagName.toLowerCase() === majorTag ? "h2" : "h3"
      // Back-matter (Chapter Summary, Key Terms, References) opts out of numbering
      // with class="no-number": it still gets an anchor + a TOC entry, but no
      // section number, and it doesn't advance the numbering sequence. A heading
      // that already carries a printed number (OpenStax `.os-number`, e.g. the
      // per-section headings of a Section Quiz page) is left un-numbered too.
      const unnumbered = !this.numberedValue || heading.classList.contains("no-number") || Boolean(heading.querySelector(".os-number"))
      let number = null

      if (unnumbered) {
        // no number, no counter change
      } else if (level === "h2") {
        h2Count++
        h3Count = 0
        number = ordinal
          ? `${h2Count}`
          : chapter ? `${chapter}.${h2Count}` : `${h2Count}.0`
      } else {
        h3Count++
        number = ordinal
          ? `${h2Count}.${h3Count}`
          : chapter ? `${chapter}.${h2Count}.${h3Count}` : `${h2Count}.${h3Count}`
      }

      // Create a slug for the anchor ID
      const slug = number
        ? `section-${number.replace(/\./g, "-")}`
        : `section-${text.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "")}`
      heading.id = slug

      // Prepend the section number to the heading text (numbered headings only)
      if (number) {
        const numberSpan = document.createElement("span")
        numberSpan.className = "section-number"
        numberSpan.textContent = `${number}  `
        heading.insertBefore(numberSpan, heading.firstChild)
      }

      tocItems.push({ number, text, slug, level, heading, unnumbered, major: h2Count, minor: h3Count })
    })

    if (!tocItems.length) return

    // Build the TOC element
    const tocNav = document.createElement("nav")
    tocNav.className = "chapter-outline"

    // Create the clickable header for collapsing
    const tocHeader = document.createElement("div")
    tocHeader.className = "chapter-outline-header"
    tocHeader.setAttribute("role", "button")
    tocHeader.setAttribute("tabindex", "0")
    tocHeader.setAttribute("aria-expanded", "true")

    const toggleTOC = () => {
      const isCollapsed = tocNav.classList.toggle("collapsed")
      tocHeader.setAttribute("aria-expanded", String(!isCollapsed))
    }

    tocHeader.addEventListener("click", toggleTOC)
    tocHeader.addEventListener("keydown", (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault()
        toggleTOC()
      }
    })

    const tocList = document.createElement("ul")
    const scrollOffset = this.scrollOffsetValue

    tocItems.forEach((item) => {
      const li = document.createElement("li")
      if (item.level === "h3") li.className = "toc-sub"

      const a = document.createElement("a")
      a.href = `#${item.slug}`
      a.innerHTML = `<span class="toc-number">${item.number || ""}</span>${item.text}`

      a.addEventListener("click", (e) => {
        e.preventDefault()
        const target = document.getElementById(item.slug)
        if (target) {
          // a heading folded away by reading focus has to be opened to be scrolled to
          target.closest("details.focus-fold")?.setAttribute("open", "")
          const top = target.getBoundingClientRect().top + window.pageYOffset - scrollOffset
          window.scrollTo({ top, behavior: "smooth" })
          history.pushState(null, null, `#${item.slug}`)
        }
      })

      li.appendChild(a)
      tocList.appendChild(li)
    })

    tocNav.appendChild(tocHeader)
    tocNav.appendChild(tocList)

    // Insert after target element, or before the first h2
    const insertTarget = wrapper.querySelector(this.insertAfterValue)
    if (insertTarget) {
      insertTarget.parentNode.insertBefore(tocNav, insertTarget.nextSibling)
    } else {
      const firstMajor = tocItems.find((item) => item.level === "h2")?.heading || tocItems[0].heading
      // A heading wrapped in a link (OpenStax end-of-chapter pages link each
      // section heading back to its section) must not get the outline
      // inserted inside the link: go up to the enclosing section instead.
      const anchor = firstMajor.parentNode.tagName === "A"
        ? firstMajor.closest("section") || firstMajor.parentNode
        : firstMajor
      anchor.parentNode.insertBefore(tocNav, anchor)
    }

    this.#applyReadingFocus(tocItems, tocList, tocNav)

    if (this.activeTrackingValue) this.#trackActiveSection(tocItems, tocList)
  }

  disconnect() {
    if (this.scrollHandler) window.removeEventListener("scroll", this.scrollHandler)
  }

  // ---- Reading focus (assigned sections) --------------------------------

  // Headings whose section is the review quiz. Their visibility follows the
  // `questions` param rather than `sections` (the chapter-quiz controller
  // filters the individual questions).
  static QUIZ_SLUGS = ["review-exercises", "suggested-solutions-to-review-exercises"]

  #applyReadingFocus(tocItems, tocList, tocNav) {
    const sectionsSpec = parseSpec(this.sectionsValue || paramFromUrl(SECTIONS_PARAM))
    const questionsSpec = parseSpec(this.questionsValue || paramFromUrl(QUESTIONS_PARAM))
    if (sectionsSpec.empty || sectionsSpec.all) return

    // Split the chapter into blocks: each heading plus the elements that follow
    // it up to the next heading. Content before the first heading (hero, key
    // terms deck, outline) is never dimmed.
    // Headings may be nested (OpenStax wraps each sub-section in its own
    // <section> inside the parent's), so a run also stops at any sibling that
    // CONTAINS a later heading — otherwise a parent's lead-in would swallow
    // its sub-sections whole.
    const blocks = tocItems.map((item, i) => {
      const nodes = [item.heading]
      let node = item.heading.nextElementSibling
      const stop = tocItems[i + 1]?.heading
      const later = tocItems.slice(i + 1).map((t) => t.heading)
      while (node && node !== stop && node !== tocNav && !later.some((h) => node.contains(h))) {
        nodes.push(node)
        node = node.nextElementSibling
      }
      const slug = item.slug.replace(/^section-/, "")
      // An h2 block is the heading + its lead-in text (before the first h3):
      // (major, 0). Its sub-sections are (major, minor).
      const tuple = item.level === "h2" ? [item.major, 0] : [item.major, item.minor]
      return { item, nodes, slug, tuple, assigned: false, headingVisible: false }
    })

    const isQuiz = (block) => this.constructor.QUIZ_SLUGS.includes(block.slug)
    let parent = null
    blocks.forEach((block) => {
      if (block.item.level === "h2") parent = block

      if (isQuiz(block)) {
        block.assigned = !questionsSpec.empty
      } else if (block.item.level === "h2") {
        // whole section by number/slug, or lead-in only via `slug:intro`
        block.assigned = specMatches(sectionsSpec, block) || specMatches(sectionsSpec, { slug: `${block.slug}:intro` })
        block.wholeSelected = specMatches(sectionsSpec, { slug: block.slug })
      } else {
        block.assigned = specMatches(sectionsSpec, block) || Boolean(parent?.wholeSelected)
        if (block.assigned && parent) parent.headingVisible = true
      }
    })
    if (!blocks.some((b) => b.assigned)) return // nothing matched: leave the chapter alone

    blocks.forEach((block) => {
      if (block.assigned) return
      block.nodes.forEach((node, i) => {
        if (i === 0 && block.headingVisible) return // parent heading of an assigned sub-section
        node.classList.add("focus-muted")
      })
    })

    Array.from(tocList.children).forEach((li, i) => {
      const block = blocks[i]
      li.classList.add(block.assigned || block.headingVisible ? "toc-assigned" : "toc-unassigned")
    })

    this.focusMode = (this.focusModeValue || paramFromUrl(FOCUS_PARAM)) === "dim" ? "dim" : "fold"
    this.element.classList.add("has-reading-focus", `focus-${this.focusMode}`)
    if (this.focusMode === "fold") this.#foldUnassigned(blocks)
    this.#addIntroEntries(blocks, tocList)
    tocNav.insertAdjacentElement("afterend", this.#focusBanner(blocks, sectionsSpec, questionsSpec))
  }

  // A heading's lead-in (the text before its first sub-section) is its own
  // assignable unit ("0.0"), but the outline only lists the heading. When the
  // lead-in and its sub-sections end up in different states — Hana's
  // `sections=0.0,0.9-0.11`, or the reverse `0.1-0.8` — add an "N.0 Intro" entry
  // under the heading so the outline shows what is actually assigned. Only then:
  // the outline is unchanged for every other reader.
  #addIntroEntries(blocks, tocList) {
    const lis = Array.from(tocList.children)
    blocks.forEach((block, i) => {
      if (block.item.level !== "h2" || block.nodes.length < 2) return
      const children = []
      for (let j = i + 1; j < blocks.length && blocks[j].item.level === "h3"; j++) children.push(blocks[j])
      if (!children.length) return
      const leadInAssigned = block.assigned
      if (children.every((c) => c.assigned === leadInAssigned)) return

      const li = document.createElement("li")
      li.className = `toc-sub toc-intro ${leadInAssigned ? "toc-assigned" : "toc-unassigned"}`
      const a = document.createElement("a")
      a.href = `#${block.item.slug}`
      a.innerHTML = `<span class="toc-number">${block.item.major}.0</span>Intro`
      a.addEventListener("click", (e) => {
        e.preventDefault()
        const heading = block.item.heading
        const fold = heading.nextElementSibling
        if (fold?.matches("details.focus-fold")) fold.setAttribute("open", "")
        const top = heading.getBoundingClientRect().top + window.pageYOffset - this.scrollOffsetValue
        window.scrollTo({ top, behavior: "smooth" })
        history.pushState(null, null, a.getAttribute("href"))
      })
      li.appendChild(a)
      lis[i].insertAdjacentElement("afterend", li)
    })
  }

  // Collapse each run of unassigned content into one <details> accordion whose
  // summary lists what is inside ("Not assigned · 0.9 Order of Operations · …").
  // A visible parent heading breaks a run, so its lead-in folds on its own right
  // under the heading. Native <details> keeps this keyboard-accessible for free.
  #foldUnassigned(blocks) {
    const groups = []
    let current = null
    blocks.forEach((block) => {
      if (block.assigned) {
        current = null
        return
      }
      const nodes = block.headingVisible ? block.nodes.slice(1) : block.nodes
      if (!nodes.length) return
      if (block.headingVisible) current = null // heading stays in the flow; lead-in starts a run
      // a fold moves its nodes into one <details>; never pull nodes out of a
      // different parent (nested sections) into it
      if (current && current.nodes[current.nodes.length - 1].parentNode !== nodes[0].parentNode) current = null
      if (!current) {
        current = { nodes: [], titles: [] }
        groups.push(current)
      }
      current.nodes.push(...nodes)
      const label = block.item.number ? `${block.item.number} ${block.item.text}` : block.item.text
      current.titles.push(block.headingVisible ? `${label} (intro)` : label)
    })

    this.folds = groups.map((group) => {
      const details = document.createElement("details")
      details.className = "focus-fold"
      const summary = document.createElement("summary")
      const kicker = document.createElement("span")
      kicker.className = "focus-fold-label"
      kicker.textContent = "Not assigned"
      const titles = document.createElement("span")
      titles.className = "focus-fold-titles"
      titles.textContent = group.titles.join(" · ")
      const hint = document.createElement("span")
      hint.className = "focus-fold-hint"
      summary.append(kicker, titles, hint)
      const body = document.createElement("div")
      body.className = "focus-fold-body"
      group.nodes[0].insertAdjacentElement("beforebegin", details)
      group.nodes.forEach((node) => body.appendChild(node))
      details.append(summary, body)
      return details
    })
  }

  #focusBanner(blocks, sectionsSpec, questionsSpec) {
    const banner = document.createElement("div")
    banner.className = "reading-focus-banner"
    banner.setAttribute("role", "status")

    const kicker = document.createElement("div")
    kicker.className = "reading-focus-kicker"
    kicker.textContent = "Assigned reading"
    banner.appendChild(kicker)

    const parts = [`Sections ${describeSpec(sectionsSpec)}`]
    if (!questionsSpec.empty) {
      parts.push(questionsSpec.all ? "all review questions" : `review questions ${describeSpec(questionsSpec)}`)
    }
    const summary = document.createElement("p")
    summary.className = "reading-focus-summary"
    summary.textContent = parts.join(" · ")
    banner.appendChild(summary)

    const note = document.createElement("p")
    note.className = "reading-focus-note"
    note.textContent = this.focusMode === "dim"
      ? "The rest of this chapter is dimmed so you can focus on what your instructor assigned."
      : "The rest of this chapter is folded up. Open any folded part, or show the whole chapter."
    banner.appendChild(note)

    const actions = document.createElement("div")
    actions.className = "reading-focus-actions"
    const first = blocks.find((b) => b.assigned)
    if (first) {
      const jump = document.createElement("a")
      jump.className = "reading-focus-jump"
      jump.href = `#${first.item.slug}`
      const label = first.item.number ? `${first.item.number} ${first.item.text}` : first.item.text
      jump.textContent = `Jump to ${label} →`
      jump.addEventListener("click", (e) => {
        e.preventDefault()
        const top = first.item.heading.getBoundingClientRect().top + window.pageYOffset - this.scrollOffsetValue
        window.scrollTo({ top, behavior: "smooth" })
        history.pushState(null, null, jump.getAttribute("href"))
      })
      actions.appendChild(jump)
    }

    const toggle = document.createElement("button")
    toggle.type = "button"
    toggle.className = "reading-focus-toggle"
    toggle.setAttribute("aria-pressed", "false")
    const showAll = "Show full chapter"
    toggle.textContent = showAll
    toggle.addEventListener("click", () => {
      const revealed = this.element.classList.toggle("focus-revealed")
      toggle.setAttribute("aria-pressed", String(revealed))
      toggle.textContent = revealed ? "Back to assigned reading" : showAll
      this.folds?.forEach((details) => (details.open = revealed))
    })
    actions.appendChild(toggle)
    banner.appendChild(actions)
    return banner
  }

  // Highlight the TOC entry for the last heading scrolled past (rAF-throttled).
  #trackActiveSection(tocItems, tocList) {
    // synthetic "Intro" entries (reading focus) have no tocItem of their own
    const lis = Array.from(tocList.children).filter((li) => !li.classList.contains("toc-intro"))
    const threshold = this.scrollOffsetValue + 150
    let ticking = false

    const update = () => {
      ticking = false
      let activeIndex = -1
      tocItems.forEach((item, i) => {
        const el = document.getElementById(item.slug)
        // skip hidden headings (offsetParent null): e.g. the Suggested Solutions
        // heading once chapter-quiz absorbs it — its rect.top of 0 would pin it active
        if (el && el.offsetParent !== null && el.getBoundingClientRect().top <= threshold) activeIndex = i
      })
      lis.forEach((li, i) => li.classList.toggle("active", i === activeIndex))
    }

    this.scrollHandler = () => {
      if (!ticking) {
        ticking = true
        requestAnimationFrame(update)
      }
    }
    window.addEventListener("scroll", this.scrollHandler, { passive: true })
    update()
  }
}
